import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useModal } from '../../contexts/ModalContext'
import Activity from '../../model/Activity'
import { ScreenPath } from '../../navigation'
import { logError } from '../../util/errorLogger'
import ActivityLogic from '../../logic/ActivityLogic'
import { loggableActivities } from '../../logic/activityAccess'
import { capRmsAtNextGate, rmsUntilNextGate } from '../../logic/gateUtils'
import { useActivities } from '../activity/useActivities'
import { useActivityLogs } from '../activity/useActivityLogs'
import { useLogActivity, type LogActivityResult } from '../activity/useLogActivity'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useActiveGate } from '../gate/useActiveGate'
import { useGates } from '../gate/useGates'
import {
  getLoggedUnitWord,
  getLogVerb,
  rmLabel,
  groupActivities,
  orderActivities,
  randomQuip,
} from './logUtils'
import { useRestDays } from '../restDays/useRestDays'
import { useStartRestDay } from '../restDays/useStartRestDay'
import { useThemeColors } from '../theme/useThemeColors'
import { useUser } from '../user/useUser'

export type LogOverlayState = 'none' | 'restConfirm' | 'restSuccess' | 'victory' | 'confirmLog'

const CONFETTI_COLOR_VARS = ['--orange', '--blue', '--green', '--orange-ink'] as const

export interface LogVictoryState {
  quip: string
  rms: number
  message: string
  result: LogActivityResult | null
}

export function useLogScreenFlow() {
  const navigate = useNavigate()
  const location = useLocation()
  const routeActivityId = location.pathname.split('/').pop()
  const isAmountRoute = routeActivityId && routeActivityId !== 'log'

  const { data: user } = useUser()
  const { data: challengeContext } = useChallengeContext()
  const { data: activities, isLoading: activitiesLoading, error } = useActivities()
  const { data: activityLogs, isLoading: logsLoading } = useActivityLogs()
  const { canStart, remaining, count, isActive, restDaysRequired } = useRestDays()
  const { mutateAsync: startRestDay } = useStartRestDay()
  const { mutate: logActivityMutation, isPending: isLogging } = useLogActivity()
  const { showSnackbar } = useModal()
  const { data: activeGate, isLoading: gateLoading } = useActiveGate()
  const { data: gates } = useGates()
  const confettiColors = useThemeColors(CONFETTI_COLOR_VARS)

  const [overlay, setOverlay] = useState<LogOverlayState>('none')
  const [victory, setVictory] = useState<LogVictoryState | null>(null)
  const [pendingLog, setPendingLog] = useState<{ activity: Activity; value: number } | null>(null)

  const teamProgress = ActivityLogic.getTotalProgress(activityLogs ?? [])
  const gateRoom = rmsUntilNextGate(gates ?? [], teamProgress)
  const openActivities = activities
    ? orderActivities(activities.filter((activity) => activity.isActive && activity.isAvailableNow()))
    : []
  const availableActivities = loggableActivities(openActivities, gates ?? [], teamProgress)

  useEffect(() => {
    if (activitiesLoading || gateLoading || logsLoading || !activities || !gates || !activityLogs) return
    if (activeGate && overlay !== 'victory') {
      navigate(`${ScreenPath.gate}/${activeGate.gate.id}`, { replace: true })
      return
    }
    if (
      isAmountRoute &&
      !availableActivities.some((activity) => activity.id === routeActivityId)
    ) {
      navigate(ScreenPath.log, { replace: true })
    }
  }, [
    activeGate,
    activities,
    overlay,
    activitiesLoading,
    activityLogs,
    availableActivities,
    gateLoading,
    gates,
    isAmountRoute,
    logsLoading,
    navigate,
    routeActivityId,
  ])

  const hasCompletedAllRestDays =
    restDaysRequired !== undefined && count >= restDaysRequired
  const challengeEnded =
    (challengeContext?.challenge.hasEnded() ?? false) && user?.isAdmin !== true

  const grouped = groupActivities(availableActivities)

  const selectedActivity =
    (isAmountRoute && availableActivities.find((activity) => activity.id === routeActivityId)) ||
    null

  function hasLoggedActivity(activityId: string): boolean {
    if (!user?.id) return false
    return (activityLogs ?? []).some((log) => log.userID === user.id && log.activityID === activityId)
  }

  function isActivityDisabled(activity: Activity): boolean {
    return activity.oneTimeUse && hasLoggedActivity(activity.id)
  }

  function selectActivity(activity: Activity) {
    if (isActivityDisabled(activity)) return
    navigate(`${ScreenPath.log}/${activity.id}`, { replace: true })
  }

  function backToSelect() {
    navigate(ScreenPath.log, { replace: true })
  }

  function resetFlow() {
    setOverlay('none')
    setVictory(null)
    setPendingLog(null)
    navigate(ScreenPath.log, { replace: true })
  }

  function buildLogMessage(activity: Activity, value: number, cutRms: number, creditedRms: number): string {
    const logged = !activity.requiresAmount
      ? `You logged <b>${getLogVerb(activity)}</b>.`
      : `You logged <b>${value} ${getLoggedUnitWord(activity.unit)}</b> of ${getLogVerb(activity)}.`
    if (Math.round(cutRms) <= 0) return logged
    const cut = Math.round(cutRms)
    const credited = Math.round(creditedRms)
    const verb = cut === 1 ? 'was' : 'were'
    return `${logged} <b>${rmLabel(cut)}</b> ${verb} cut off at the gate, so <b>${rmLabel(credited)}</b> counted.`
  }

  function performLog(activity: Activity, value: number) {
    if (isLogging) return

    const logValue = activity.requiresAmount ? value : 1
    const proposedRms = logValue * activity.unitConversion
    const { creditedRms, cutRms } = capRmsAtNextGate(proposedRms, gateRoom)
    const rms = Math.round(creditedRms)

    setVictory({
      quip: randomQuip(),
      rms,
      message: buildLogMessage(activity, logValue, cutRms, creditedRms),
      result: null,
    })
    setOverlay('victory')

    logActivityMutation(
      { activity, value: logValue },
      {
        onSuccess: (result) => {
          setVictory((current) => (current ? { ...current, result } : null))
        },
        onError: (err: Error) => {
          setOverlay('none')
          setVictory(null)
          showSnackbar('log-activity-error', err.message || 'Failed to log activity', 'error')
          void logError(err, { screen: 'LogScreen', action: 'logActivity' })
        },
      },
    )
  }

  function submitLog(activity: Activity, value: number) {
    if (isLogging) return

    const proposedRms = (activity.requiresAmount ? value : 1) * activity.unitConversion
    const { cutRms } = capRmsAtNextGate(proposedRms, gateRoom)
    const tooMuch = activity.requiresAmount && value > activity.alertThreshold
    if (tooMuch || Math.round(cutRms) > 0) {
      setPendingLog({ activity, value })
      setOverlay('confirmLog')
      return
    }

    performLog(activity, value)
  }

  async function confirmRestDay() {
    try {
      await startRestDay()
      setOverlay('restSuccess')
    } catch (err) {
      void logError(err, { screen: 'LogScreen', action: 'startRestDay' })
    }
  }

  const earnedBadge = victory?.result?.earnedBadges[0] ?? victory?.result?.earnedTeamBadges[0]

  return {
    challengeContext,
    challengeEnded,
    isLoading: activitiesLoading || gateLoading || logsLoading,
    gateRoom,
    error,
    activities,
    canStart,
    remaining,
    count,
    isActive,
    restDaysRequired,
    hasCompletedAllRestDays,
    grouped,
    selectedActivity,
    overlay,
    setOverlay,
    victory,
    pendingLog,
    setPendingLog,
    confettiColors,
    earnedBadge,
    selectActivity,
    backToSelect,
    resetFlow,
    submitLog,
    performLog,
    confirmRestDay,
    isActivityDisabled,
    navigate,
  }
}
